import { NavLink } from 'react-router-dom';
import { useCompetition } from '@/hooks/useCompetitions';
import { Badge } from '@/components/ui/Badge';
import { competitionStatusLabel, competitionStatusTone, FZS_PATRONAGE_LABEL } from '@/lib/competitions';

/** The organizer's pages of ONE competition, in the order they are needed; everything per-competition lives here, not in the sidebar. */
export function competitionWorkspaceTabs(slug: string) {
  const org = `/organizer/competitions/${slug}`;
  return [
    { label: 'Pregled', to: `/competitions/${slug}`, end: true },
    { label: 'Nastavitve', to: `${org}/setup` },
    { label: 'Udeleženci', to: `${org}/participants` },
    { label: 'Nagrade', to: `${org}/awards` },
  ];
}

export function CompetitionWorkspaceTabs({ competitionId }: { competitionId: string }) {
  const competition = useCompetition(competitionId || undefined);
  if (!competition.data) return null;
  return (
    <nav aria-label="Strani tekmovanja" className="flex flex-wrap gap-1 border-b border-line">
      {competitionWorkspaceTabs(competition.data.slug).map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            `-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-fast ${
              isActive ? 'border-accent text-fg' : 'border-transparent text-fg-secondary hover:text-fg'
            }`
          }
        >
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * Header of an organizer page inside one competition: name, status and the tabs. `title` names the page itself
 * (Nagrade, Udeleženci, …) so the competition name is not repeated in every heading.
 */
export function CompetitionWorkspaceHeader({ competitionId, title, description }: { competitionId: string; title: string; description?: string }) {
  const competition = useCompetition(competitionId || undefined);
  const c = competition.data;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        {c?.logoUrl ?? c?.organizationLogoUrl ? (
          <img src={c?.logoUrl ?? c?.organizationLogoUrl ?? ''} alt="" className="h-10 w-10 rounded-photo object-contain" />
        ) : null}
        <h1 className="text-xl font-semibold text-fg">{c?.name ?? '…'}</h1>
        {c && <Badge tone={competitionStatusTone(c.status)}>{competitionStatusLabel(c.status)}</Badge>}
        {c?.isFzsPatronage && <Badge tone="Fzs">{FZS_PATRONAGE_LABEL}</Badge>}
      </div>
      <CompetitionWorkspaceTabs competitionId={competitionId} />
      <div>
        <h2 className="text-lg font-semibold text-fg">{title}</h2>
        {description && <p className="text-sm text-fg-muted">{description}</p>}
      </div>
    </div>
  );
}
